import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

const PasswordStrengthMeter = ({ password }) => {
  const { t } = useTranslation();

  // Password requirements
  const requirements = {
    length: password.length >= 12,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'`~;]/.test(password)
  };

  // Calculate strength
  const metRequirements = Object.values(requirements).filter(Boolean).length;
  const strength = metRequirements === 0 ? 0 : Math.ceil((metRequirements / 5) * 100);

  // Determine strength level
  let strengthLevel = 'weak';
  let strengthColor = 'bg-red-500';
  let strengthText = t('register.password.strength.weak');

  if (strength >= 80) {
    strengthLevel = 'strong';
    strengthColor = 'bg-green-500';
    strengthText = t('register.password.strength.strong');
  } else if (strength >= 60) {
    strengthLevel = 'medium';
    strengthColor = 'bg-yellow-500';
    strengthText = t('register.password.strength.medium');
  }

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs font-medium text-gray-700">
            {t('register.password.strength.label')}
          </span>
          <span className={`text-xs font-semibold ${
            strengthLevel === 'strong' ? 'text-green-600' :
            strengthLevel === 'medium' ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {strengthText}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${strengthColor}`}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="grid grid-cols-1 gap-1.5 text-xs">
        <RequirementItem 
          met={requirements.length}
          text={t('register.password.requirements.length')}
        />
        <RequirementItem 
          met={requirements.uppercase}
          text={t('register.password.requirements.uppercase')}
        />
        <RequirementItem 
          met={requirements.lowercase}
          text={t('register.password.requirements.lowercase')}
        />
        <RequirementItem 
          met={requirements.number}
          text={t('register.password.requirements.number')}
        />
        <RequirementItem 
          met={requirements.special}
          text={t('register.password.requirements.special')}
        />
      </div>
    </div>
  );
};

const RequirementItem = ({ met, text }) => {
  return (
    <div className={`flex items-center gap-2 transition-colors ${
      met ? 'text-green-600' : 'text-gray-500'
    }`}>
      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
        met ? 'bg-green-100' : 'bg-gray-100'
      }`}>
        {met ? (
          <Check className="w-3 h-3" strokeWidth={3} />
        ) : (
          <X className="w-3 h-3" strokeWidth={2} />
        )}
      </div>
      <span className="font-medium">{text}</span>
    </div>
  );
};

export default PasswordStrengthMeter;